import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Equipment,
  ListAssignmentsResponse,
  ParticipantEquipmentAssignment,
} from 'shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

const BASE = '/api/participant-equipment-assignments';

export const reservationKeys = {
  all: ['reservations'] as const,
  myActive: (participantId: number) =>
    ['reservations', 'mine', participantId, 'active'] as const,
  myFinished: (participantId: number) =>
    ['reservations', 'mine', participantId, 'finished'] as const,
};

/**
 * Refetch cadence for the participant's open reservations. Sessions with a
 * time limit are ended by the API on its own (every minute), and a pool's
 * free units change as other participants scan, so the list must poll rather
 * than rely on focus/navigation refetches.
 */
export const ACTIVE_RESERVATIONS_REFETCH_MS = 30_000;

/**
 * GET /api/participant-equipment-assignments — open reservations for the
 * current participant. The contract:
 *   status=confirmed  → assignment is not cancelled/deleted
 *   endedAtNull=true  → assignment has not been returned or auto-ended yet
 * The equipment's estado is deliberately NOT a filter: a pooled equipment
 * (quantidade > 1) stays "Ativo" while it still has free units, even though
 * this participant holds one of them.
 *
 * `staleTime` defaults to 30s; the Início and Reservas screens pass a shorter
 * window so reservation changes made elsewhere surface on the next refetch.
 */
export function useMyActiveReservations(
  participantId: number | undefined,
  staleTime = 30_000,
) {
  const { isAuthenticated, kind } = useAuthStore();
  const enabled =
    isAuthenticated &&
    kind === 'participant' &&
    typeof participantId === 'number' &&
    participantId > 0;

  return useQuery({
    queryKey:
      typeof participantId === 'number'
        ? reservationKeys.myActive(participantId)
        : ['reservations', 'mine', 'unauthenticated', 'active'],
    queryFn: () => {
      const query = new URLSearchParams({
        participantId: String(participantId),
        status: 'confirmed',
        endedAtNull: 'true',
        limit: '100',
      });
      return api.get<ListAssignmentsResponse>(`${BASE}?${query.toString()}`);
    },
    enabled,
    staleTime,
    refetchInterval: ACTIVE_RESERVATIONS_REFETCH_MS,
  });
}

/**
 * GET /api/participant-equipment-assignments — the reservation history for the
 * current participant: every confirmed assignment, finished or still open.
 *   status=confirmed → assignment is not cancelled/deleted
 * No endedAt filter — the Histórico screen lists open reservations too and
 * labels each row from `endedAt` ("Em curso" vs "Concluída"). Equipment state
 * is intentionally not filtered — a past reservation is decoupled from
 * whatever the equipment is doing right now.
 */
export function useMyFinishedReservations(participantId: number | undefined) {
  const { isAuthenticated, kind } = useAuthStore();
  const enabled =
    isAuthenticated &&
    kind === 'participant' &&
    typeof participantId === 'number' &&
    participantId > 0;

  return useQuery({
    queryKey:
      typeof participantId === 'number'
        ? reservationKeys.myFinished(participantId)
        : ['reservations', 'mine', 'unauthenticated', 'finished'],
    queryFn: () => {
      const query = new URLSearchParams({
        participantId: String(participantId),
        status: 'confirmed',
        limit: '100',
      });
      return api.get<ListAssignmentsResponse>(`${BASE}?${query.toString()}`);
    },
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Payload returned by the atomic scan endpoint. `action` tells the UI which
 * transition happened so it can show the right confirmation banner.
 */
export interface ScanReservationResponse {
  action: 'started' | 'ended';
  assignment: ParticipantEquipmentAssignment;
  equipment: Equipment;
}

/**
 * POST /api/participant-equipment-assignments/scan.
 * Backend decides start vs end atomically based on the equipment's current
 * state. On success, refresh reservations and equipment caches.
 */
export function useScanEquipment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ equipmentId }: { equipmentId: number }) =>
      api.post<ScanReservationResponse>(`${BASE}/scan`, { equipmentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['equipments'] });
    },
  });
}
